import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Gauge, Mic2, CalendarClock, Handshake, Sparkles, ListTodo, Wallet, Megaphone, Inbox, Settings2, CornerDownLeft, Compass, BookOpen, Presentation } from "lucide-react";
import { api } from "@/lib/api";

const SIDER = [
  { label: "Overview", sub: "Control Center", to: "/admin/overview", Ico: Gauge },
  { label: "Playbook", sub: "Project Playbook · levende strategidokument", to: "/admin/playbook", Ico: BookOpen },
  { label: "Strategy", sub: "Strategy Board · retning & valg", to: "/admin/strategy", Ico: Compass },
  { label: "Speakers", sub: "Pipeline · readiness", to: "/admin/speakers", Ico: Mic2 },
  { label: "Program", sub: "Tidslinje · sessions", to: "/admin/program", Ico: CalendarClock },
  { label: "Partners", sub: "Avtaler · fulfillment", to: "/admin/partners", Ico: Handshake },
  { label: "Partner decks", sub: "Headliner · Build · lenker per mottaker", to: "/admin/deck", Ico: Presentation },
  { label: "Experience", sub: "Expo · middager · aktiveringer", to: "/admin/experience", Ico: Sparkles },
  { label: "Saker", sub: "Oppgaver · frister", to: "/admin/saker", Ico: ListTodo },
  { label: "Budget", sub: "Financial OS · overview", to: "/admin/budsjett", Ico: Wallet },
  { label: "Budget · Revenue", sub: "Physical · digital · dinner · partners", to: "/admin/budsjett/revenue", Ico: Wallet },
  { label: "Budget · Costs", sub: "Driver-based cost lines", to: "/admin/budsjett/costs", Ico: Wallet },
  { label: "Budget · Cash Flow", sub: "Liquidity · funding", to: "/admin/budsjett/cashflow", Ico: Wallet },
  { label: "Budget · Scenarios", sub: "Downside · Base · Upside", to: "/admin/budsjett/scenarios", Ico: Wallet },
  { label: "Announcements", sub: "Nettside", to: "/admin/announcements", Ico: Megaphone },
  { label: "Signups", sub: "Early Access · partnerhenvendelser", to: "/admin/signups", Ico: Inbox },
  { label: "Settings", sub: "Hero copy · event mode", to: "/admin/settings", Ico: Settings2 },
];

export default function CommandPalette({ open, onClose }) {
  const [q, setQ] = useState("");
  const [aktiv, setAktiv] = useState(0);
  const [entiteter, setEntiteter] = useState(null);
  const nav = useNavigate();
  const inputRef = useRef(null);
  const listeRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setQ("");
    setAktiv(0);
    setTimeout(() => inputRef.current?.focus(), 30);
    if (entiteter == null) {
      Promise.allSettled([
        api.get("/admin/os/speakers"),
        api.get("/admin/os/partners"),
        api.get("/admin/os/experiences"),
        api.get("/admin/tasks"),
      ]).then(([sp, pa, ex, ta]) => {
        const ut = [];
        if (sp.status === "fulfilled") {
          for (const s of sp.value.data.speakers || []) {
            ut.push({ gruppe: "Speakers", label: s.person?.name || "", sub: s.person?.org || "", to: "/admin/speakers" });
          }
        }
        if (pa.status === "fulfilled") {
          for (const p of pa.value.data.partners || []) {
            ut.push({ gruppe: "Partnere", label: p.company?.name || "", sub: p.packageName || "", to: "/admin/partners" });
          }
        }
        if (ex.status === "fulfilled") {
          for (const e of ex.value.data.experiences || []) {
            ut.push({ gruppe: "Experiences", label: e.name, sub: e.type, to: "/admin/experience" });
          }
        }
        if (ta.status === "fulfilled") {
          for (const t of (ta.value.data.tasks || []).slice(0, 100)) {
            ut.push({ gruppe: "Saker", label: t.title, sub: t.status, to: "/admin/saker" });
          }
        }
        setEntiteter(ut);
      });
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const resultater = useMemo(() => {
    const s = q.trim().toLowerCase();
    const sider = SIDER.filter((x) => !s || x.label.toLowerCase().includes(s) || x.sub.toLowerCase().includes(s))
      .map((x) => ({ ...x, gruppe: "Gå til" }));
    if (!s) return sider;
    const treff = (entiteter || []).filter((x) => x.label.toLowerCase().includes(s)).slice(0, 12);
    return [...sider, ...treff];
  }, [q, entiteter]);

  const velg = useCallback((item) => {
    if (!item) return;
    onClose();
    nav(item.to);
  }, [nav, onClose]);

  useEffect(() => {
    if (!open) return;
    const key = (e) => {
      if (e.key === "Escape") { e.preventDefault(); onClose(); }
      else if (e.key === "ArrowDown") { e.preventDefault(); setAktiv((i) => Math.min(i + 1, resultater.length - 1)); }
      else if (e.key === "ArrowUp") { e.preventDefault(); setAktiv((i) => Math.max(i - 1, 0)); }
      else if (e.key === "Enter") { e.preventDefault(); velg(resultater[aktiv]); }
    };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [open, resultater, aktiv, velg, onClose]);

  useEffect(() => {
    listeRef.current?.querySelector('[data-aktiv="1"]')?.scrollIntoView({ block: "nearest" });
  }, [aktiv]);

  if (!open) return null;

  let forrigeGruppe = null;
  return (
    <div className="fixed inset-0 z-[70] flex items-start justify-center pt-[14vh] px-4" data-testid="cmdk-overlay">
      <div className="adm-cmdk-backdrop absolute inset-0" onClick={onClose} />
      <div className="adm-cmdk relative w-full max-w-[580px]">
        <div className="flex items-center gap-3 px-4 border-b border-white/[0.08]">
          <Search size={15} className="adm-cmdk-dim shrink-0" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => { setQ(e.target.value); setAktiv(0); }}
            placeholder="Søk i Event OS — sider, speakers, partnere, saker …"
            className="adm-cmdk-in flex-1 bg-transparent py-3.5 text-[14px] focus:outline-none"
            data-testid="cmdk-input"
          />
          <kbd className="adm-kbd">esc</kbd>
        </div>
        <div ref={listeRef} className="max-h-[46vh] overflow-y-auto py-2">
          {resultater.length === 0 && (
            <p className="adm-cmdk-dim px-4 py-8 text-center text-[12.5px]" data-testid="cmdk-tom">Ingen treff.</p>
          )}
          {resultater.map((r, i) => {
            const nyGruppe = r.gruppe !== forrigeGruppe;
            forrigeGruppe = r.gruppe;
            const Ico = r.Ico;
            return (
              <div key={`${r.gruppe}-${r.label}-${i}`}>
                {nyGruppe && (
                  <p className="adm-cmdk-dim px-4 pt-3 pb-1.5 text-[9.5px] font-semibold uppercase tracking-[0.24em]">{r.gruppe}</p>
                )}
                <button
                  type="button"
                  data-aktiv={i === aktiv ? "1" : "0"}
                  onMouseEnter={() => setAktiv(i)}
                  onClick={() => velg(r)}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${i === aktiv ? "adm-cmdk-aktiv" : ""}`}
                  data-testid={`cmdk-item-${i}`}
                >
                  {Ico ? <Ico size={14} className="adm-cmdk-ikon shrink-0" strokeWidth={1.75} /> : <span className="w-[14px] shrink-0" />}
                  <span className="adm-cmdk-txt text-[13.5px] truncate">{r.label}</span>
                  {r.sub && <span className="adm-cmdk-dim text-[11px] truncate">{r.sub}</span>}
                  {i === aktiv && <CornerDownLeft size={12} className="adm-cmdk-dim ml-auto shrink-0" />}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
